import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import BundleCount from 'honeydew/BundleCount';
import Button from 'honeydew/Button';
import AutoFilledIcon from 'honeydew/icons/FoodPreferences/AutoFilledIcon';
import RecurringIcon from 'honeydew/icons/FoodPreferences/RecurringIcon';
import QuantityButton from 'honeydew/QuantityButton';
import Tag from 'honeydew/Tag';
import useGlobalStore from 'hooks/useGlobalStore';
import useProductSelectionData from 'hooks/useProductSelectionData';
import useTailwindTheme from 'hooks/useTailwindTheme';
import useUpdateProductQty from 'hooks/useUpdateProductQty';
import { getProductPrice, PriceType } from 'utils/priceUtils';
import { getProductOriginalPrice, getShowOriginalPrice } from 'utils/productUtils';
import { addProduct } from 'utils/removedFromCartStorageUtils';

import { getPlusMembershipPreviewPrice } from './utils';

function CartProductCard({
    dsSource,
    ebtCard,
    onClick,
    plusMembershipEligibleOrder,
    plusMembershipShortBranding,
    position,
    product,
    upSellsItem,
    usePlusMemberPrice,
}) {
    const { setCartOpen } = useGlobalStore();
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const {
        decrementProductQty,
        incrementProductQty,
        isLoading: updateProductQtyLoading,
    } = useUpdateProductQty({
        dsSource,
        isGwp: !!product.isGwpFree,
        productID: product.id,
        productName: product.name,
        cart: !upSellsItem,
        upSellsSource: upSellsItem,
        plusMemberPriceEligible: product.plusMemberPriceEligible,
    });
    const { data: productSelectionData = {} } = useProductSelectionData({
        isGwp: !!product.isGwpFree,
        productID: product.id,
    });

    const { qty: productQty = 0 } = productSelectionData;

    const productIsRecurring = product.source === 'recurring';
    const productIsAutomatic =
        product.source === 'personalization' ||
        product.source === 'autopilot_risk' ||
        product.source === 'paid_autoship';

    const handleAddClick = (e) => {
        e.stopPropagation();
        incrementProductQty(upSellsItem ?? 'cart');
    };

    const handleRemoveClick = (e) => {
        e.stopPropagation();
        decrementProductQty(upSellsItem ?? 'cart');

        if (product.qty <= 1 && productIsAutomatic && !product.isBundle) {
            addProduct(product);
        }
    };

    const handleEditClick = (e) => {
        e.stopPropagation();
        if (window.isNativeApp) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ navigate: 'Perks' }));
        } else {
            setCartOpen(false);
        }
    };
    const showPlusMemberPrice = usePlusMemberPrice && product.plusMemberPriceEligible && plusMembershipEligibleOrder;

    const showMembershipPreviewPrice =
        product.plusMemberPriceEligible && !plusMembershipEligibleOrder && !product.isGwpFree;

    const displayProductQuantity = productQty > 0 ? productQty : 1;

    const priceType = plusMembershipEligibleOrder ? PriceType.PLUS_MEMBER : PriceType.DEFAULT;

    const productPrice =
        getProductPrice({
            options: { priceType },
            product,
        }) * displayProductQuantity;

    const productOriginalPrice = getProductOriginalPrice(product, showPlusMemberPrice) * displayProductQuantity;
    const showOriginalPrice = getShowOriginalPrice(product, 0, showPlusMemberPrice);

    const getMaxQuantity = () => {
        if (product.isGwpFree) return 1;
        if (product.soldOut) return productQty;
        return product.maxQuantity;
    };

    return (
        <div className="flex items-start gap-20" onClick={onClick} onKeyDown={onClick} role="button" tabIndex={0}>
            <div className="relative shrink-0">
                <img
                    className={clsx('h-80 w-80 rounded-lg', {
                        'h-[105px] w-[105px]': upSellsItem && !mobile,
                    })}
                    alt={product.name}
                    src={product.photoSmall}
                />
                {(productIsAutomatic || productIsRecurring) && (
                    <div
                        className={clsx(
                            'absolute top-0 left-0 flex h-[26px] w-[26px] items-center justify-center rounded-full',
                            {
                                'bg-[#CFE5BD] if:bg-strawberry': productIsAutomatic,
                                'bg-[#FAEAC0]': productIsRecurring,
                            }
                        )}
                    >
                        {productIsRecurring && <RecurringIcon width={18} />}
                        {productIsAutomatic && <AutoFilledIcon width={18} brandColors />}
                    </div>
                )}
                {product.isBundle && (
                    <BundleCount
                        data-testid="bundle-count"
                        count={product.portion}
                        width={28}
                        className="absolute top-5 right-5"
                    />
                )}
            </div>
            <div
                className={clsx('flex grow justify-between gap-20 align-top', {
                    'py-5 pr-5': upSellsItem && !mobile,
                })}
            >
                <div className="flex flex-col">
                    {product.isBundle && <p className="text-body-xs font-grotesk-bold text-error">BUNDLE</p>}
                    {product.isPerk && (
                        <p className="text-body-xs text-brand-primary font-grotesk-bold uppercase">Perk</p>
                    )}
                    {product.brand && (
                        <p
                            className={clsx('text-body-sm text-grey', {
                                'line-clamp-1': position === 'sheet',
                            })}
                        >
                            {product.brand}
                        </p>
                    )}
                    <p
                        className={clsx('text-body-sm', {
                            'mb-10': !product.isPerk && !product.isGwpFree,
                            'mb-5': upSellsItem && !mobile,
                            'line-clamp-2': position !== 'sheet',
                            'line-clamp-1': position === 'sheet',
                        })}
                    >
                        {product.name}
                    </p>
                    {showMembershipPreviewPrice && (
                        <p className="text-body-sm mb-10 text-cranberry">
                            ${getPlusMembershipPreviewPrice(product)} with{' '}
                            <Tag bgColor="cranberry" variant="dark">
                                {plusMembershipShortBranding}
                            </Tag>
                        </p>
                    )}
                    {product.isPerk ? (
                        <div className="flex items-center">
                            <p className="text-body-sm mr-10 font-grotesk-bold">Qty: {product.qty}</p>
                            <Button
                                {...(!window.isNativeApp && { as: 'link' })}
                                className="text-brand-primary text-body-sm mt-[3px] font-grotesk-bold underline"
                                to="/shop/browse/misfits+perks/redeemed"
                                onClick={handleEditClick}
                            >
                                Edit
                            </Button>
                        </div>
                    ) : (
                        <QuantityButton
                            disabled={updateProductQtyLoading}
                            maxQuantity={getMaxQuantity()}
                            onAdd={handleAddClick}
                            onRemove={handleRemoveClick}
                            quantity={productQty}
                            removeAdd={product.isGwpFree}
                            variant="secondary"
                            fullWidth={false}
                        />
                    )}
                </div>
                <div className="flex shrink-0 items-start gap-5 text-right">
                    {product.isSnapEligible && ebtCard?.last4 && (
                        <div className="mt-[2px] shrink-0">
                            <img
                                src="https://img-cdn.misfitsmarket.com/boysenberry/logos/snap-logo.svg"
                                alt="SNAP-Eligible item"
                                width={18}
                                height={15}
                            />
                        </div>
                    )}

                    {product.isGwpFree && (
                        <p className="text-body-sm text-brand-primary font-grotesk-bold uppercase">Free Gift</p>
                    )}
                    {product.isPerk && !product.isGwpFree && (
                        <div>
                            <p className="text-body-sm font-grotesk-bold">$0.00</p>
                            <p className="text-body-sm text-brand-primary uppercase">Free</p>
                        </div>
                    )}
                    {!product.isGwpFree && !product.isPerk && (
                        <div className="shrink-0">
                            <div className="flex items-baseline gap-5 text-right">
                                {showPlusMemberPrice && (
                                    <Tag bgColor="cranberry" variant="dark">
                                        {plusMembershipShortBranding}
                                    </Tag>
                                )}
                                <p
                                    className={clsx('text-body-sm font-grotesk-bold leading-[1.2rem]!', {
                                        'text-cranberry': showPlusMemberPrice,
                                        'text-error': product.isBundle,
                                    })}
                                    data-testid="cart-product-card-price"
                                >
                                    {product.isBundle && (
                                        <>
                                            {product.portion * (productQty || 1)} for
                                            <br />{' '}
                                        </>
                                    )}
                                    ${productPrice.toFixed(2)}
                                </p>
                            </div>{' '}
                            {showOriginalPrice && (
                                <p
                                    className="text-body-sm text-grey line-through"
                                    data-testid="cart-product-card-original-price"
                                >
                                    ${productOriginalPrice.toFixed(2)}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CartProductCard;

CartProductCard.propTypes = {
    dsSource: PropTypes.string,
    ebtCard: PropTypes.shape({
        last4: PropTypes.string,
    }),
    plusMembershipEligibleOrder: PropTypes.bool,
    plusMembershipShortBranding: PropTypes.string,
    position: PropTypes.string,
    product: PropTypes.shape({
        brand: PropTypes.string,
        bundleID: PropTypes.number,
        id: PropTypes.number,
        isGwpFree: PropTypes.bool,
        msrp: PropTypes.number,
        name: PropTypes.string,
        photoSmall: PropTypes.string,
        portion: PropTypes.number,
        price: PropTypes.number,
        qty: PropTypes.number,
        source: PropTypes.string,
    }).isRequired,
    upSellsItem: PropTypes.string,
    usePlusMemberPrice: PropTypes.bool,
};

CartProductCard.defaultProps = {
    dsSource: '',
    ebtCard: null,
    plusMembershipEligibleOrder: false,
    plusMembershipShortBranding: undefined,
    position: '',
    upSellsItem: null,
    usePlusMemberPrice: false,
};
